import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'
import { pickOgImage } from '@/lib/seo/images'
import { robotsDirective } from '@/lib/seo/metadata'
import { canonicalOrigin, canonicalPath, canonicalUrl } from '@/lib/seo/canonical'
import { serializeJsonLd } from '@/lib/seo/jsonld'

/**
 * EVERY PUBLIC PAGE'S METADATA, emitted from one component.
 *
 * There is no second place in this codebase that writes a title, a canonical
 * tag or an Open Graph property, and that is deliberate: metadata duplicated
 * per page drifts per page, and the failure is invisible until somebody pastes
 * a link into a chat and gets a grey box.
 *
 * Three things it guarantees that a hand-written `<Head>` does not:
 *
 *   1. THE IMAGE IS ALWAYS ABSOLUTE AND ALWAYS PRESENT. A crawler reads the
 *      SERVER's HTML, where `window.location` does not exist, so a relative
 *      `og:image` resolves against nothing. `pickOgImage` absolutises whatever
 *      it is given and falls back to the approved Bazio lockup.
 *   2. THE CANONICAL NAMES ONE HOST. Pages build their canonical from the
 *      request's own origin, which on production can be either the apex or
 *      `www`. Both are collapsed onto the canonical origin here, so the same
 *      page cannot present two different canonical URLs to two crawlers.
 *   3. ONE TITLE SHAPE. `{page name} - {site name}`, with the site name stated
 *      once and a hyphen as the separator, so the home page is the only page
 *      whose title is the bare brand.
 */

/** Rebuilds a page-supplied canonical onto the one production host. */
const normaliseCanonical = (canonical: string | undefined, baseUrl: string | null | undefined): string | null => {
    if (!canonical) return null

    try {
        const parsed = new URL(canonical)

        return canonicalUrl(parsed.origin, `${parsed.pathname}${parsed.search}`)
    } catch {
        // A path rather than a URL. Still resolvable, and still worth emitting.
        return canonical.startsWith('/') ? canonicalUrl(baseUrl, canonical) : null
    }
}

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    ogImageAlt,
    ogImageWidth,
    ogImageHeight,
    canonical,
    canonicalPage,
    noindex,
    ogType = 'website',
    baseUrl,
    locale,
    jsonLd
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const bare = normaliseCanonical(canonical, baseUrl)
    const href = bare && canonicalPage && canonicalPage > 1 ? `${bare}?page=${Math.floor(canonicalPage)}` : bare

    /*
     * The share image, resolved through the one hierarchy: this page's own
     * artwork when it has some, the Bazio lockup when it does not. Dimensions
     * ride along only when the caller actually knew them.
     */
    const image = pickOgImage(baseUrl, [
        ogImage
            ? { url: ogImage, alt: ogImageAlt ?? title ?? siteConfig.name, width: ogImageWidth, height: ogImageHeight }
            : null
    ])

    /*
     * `og:url` must be the page's own address. Without a canonical to work
     * from, the site origin is the honest answer — never a stale one from a
     * different page.
     */
    const url = href ?? canonicalOrigin(baseUrl)
    const graph = Array.isArray(jsonLd)
        ? (jsonLd.length > 0 ? { '@context': 'https://schema.org', '@graph': jsonLd } : null)
        : jsonLd ?? null

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta name='robots' content={robotsDirective(noindex)} />

            {href && <link rel='canonical' href={href} />}

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content={ogType} />
            <meta property='og:url' content={url} />
            <meta property='og:locale' content={locale || 'en'} />

            <meta property='og:image' content={image.url} />
            <meta property='og:image:alt' content={image.alt} />
            {image.width !== null && <meta property='og:image:width' content={String(image.width)} />}
            {image.height !== null && <meta property='og:image:height' content={String(image.height)} />}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image.url} />
            <meta name='twitter:image:alt' content={image.alt} />

            {/* Escaped for HTML by `serializeJsonLd`: seller text must never close this element. */}
            {graph && (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serializeJsonLd(graph) }}
                />
            )}
        </Head>
    )
}
